import { Link } from "react-router-dom";
import { ArrowRight, Check, UserCheck, Cpu, Layers, Target, MessageSquare, Globe } from "lucide-react";
import Reveal from "@/components/reveal";

const services = [
  { num: "01", title: "Permanent Recruitment", desc: "For critical, long-term specialist hires." },
  { num: "02", title: "Contract & Interim Talent", desc: "Flexible specialist expertise for projects, temporary gaps and changing business needs." },
  { num: "03", title: "Hiring & Talent Advisory", desc: "Support with role definition, hiring strategy, market understanding and specialist recruitment challenges." },
];

const reasons = [
  { icon: UserCheck, title: "Founder-Led & Personal", desc: "Work directly with the person leading your search." },
  { icon: Cpu, title: "Technical Understanding", desc: "A recruitment partner who understands technical environments and specialist roles." },
  { icon: Layers, title: "Quality Over Quantity", desc: "Focused searches and relevant candidates." },
  { icon: Target, title: "Targeted Talent Search", desc: "Going beyond job ads to reach specialists who may not be actively applying." },
  { icon: MessageSquare, title: "Honest Market Feedback", desc: "Clear conversations about talent availability and market realities." },
  { icon: Globe, title: "Netherlands-Based. Europe-Connected.", desc: "Supporting specialist hiring across the Netherlands and Europe." },
];

const Employers = () => (
  <main data-testid="employers-page">
    {/* HERO (dark) */}
    <section className="relative bg-[#0B132B] overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="absolute inset-0 orange-glow" />
      <div className="container-nb relative pt-36 pb-20 md:pt-44 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow mb-6">For Employers</p>
            <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.12] text-[#F8FAFC] max-w-3xl">
              Flexible support for specialist hiring.
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#94A3B8] leading-relaxed max-w-xl">
              Focused search and personal recruitment partnership across Cybersecurity, Cloud and Infrastructure.
            </p>
          </Reveal>
          <div className="hidden lg:block lg:col-span-5" data-testid="employers-hero-visual" aria-hidden="true">
            <Reveal delay={0.15}>
              <svg viewBox="0 0 440 400" className="w-full max-w-md mx-auto">
                {[[220,200,70,80],[220,200,170,60],[220,200,330,70],[220,200,100,190],[220,200,350,180],[220,200,80,300],[220,200,190,320],[220,200,330,320],[220,200,260,110],[220,200,150,250],[220,200,290,250],[170,60,260,110],[260,110,330,70],[100,190,150,250],[290,250,350,180],[80,300,190,320]].map(([x1,y1,x2,y2],i) => (
                  <line key={`e${i}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#24304A" strokeWidth="1" />
                ))}
                {[[70,80],[170,60],[330,70],[100,190],[350,180],[80,300],[190,320],[330,320],[260,110],[150,250],[290,250]].map(([x,y],i) => (
                  <circle key={`n${i}`} cx={x} cy={y} r="4" fill="#64748B" opacity="0.85" />
                ))}
                <circle cx="220" cy="200" r="26" fill="none" stroke="#FF5E1A" strokeOpacity="0.18" strokeWidth="1" />
                <circle cx="220" cy="200" r="15" fill="none" stroke="#FF5E1A" strokeOpacity="0.35" strokeWidth="1" />
                <circle cx="220" cy="200" r="7" fill="#FF5E1A" />
              </svg>
            </Reveal>
          </div>
        </div>
      </div>
    </section>

    {/* SERVICES (light) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-5">How We Support You</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#0F172A]">
            Three ways to work together.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <Reveal key={s.num} delay={i * 0.08}>
              <div className="card-light p-8 h-full transition-transform duration-300 hover:-translate-y-1" data-testid={`employers-service-${s.num}`}>
                <p className="font-mono-accent text-sm font-semibold text-[#FF5E1A] mb-5">{s.num}</p>
                <h3 className="font-display text-lg md:text-xl font-semibold text-[#0F172A] mb-3">{s.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* WHY WORK WITH US (dark) */}
    <section className="relative bg-[#0F172A] overflow-hidden">
      <div className="absolute inset-0 grid-overlay" />
      <div className="container-nb relative py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-5">Why Work With NicheBytes</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#F8FAFC]">
            A partner, not a CV pipeline.
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.06}>
              <div className="card-dark p-7 h-full" data-testid={`employers-reason-${i + 1}`}>
                <r.icon size={20} className="text-[#FF5E1A] mb-4" />
                <h3 className="font-display text-base md:text-lg font-semibold text-[#F8FAFC] mb-2 leading-snug">{r.title}</h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{r.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* PROCESS (light, editorial) */}
    <section className="bg-[#FAF9F5]">
      <div className="container-nb py-20 md:py-28">
        <Reveal className="max-w-2xl">
          <p className="eyebrow mb-5">How We Work</p>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight leading-snug text-[#0F172A]">
            A clear process. Built around your hiring challenge.
          </h2>
        </Reveal>
        <div className="mt-12 max-w-4xl">
          {[
            { num: "01", title: "Understand", desc: "Understand the role, team and technical environment." },
            { num: "02", title: "Map", desc: "Analyse the market and identify relevant talent." },
            { num: "03", title: "Search", desc: "Build a focused search and outreach strategy." },
            { num: "04", title: "Engage", desc: "Have meaningful conversations with relevant professionals." },
            { num: "05", title: "Present", desc: "Present a focused shortlist of relevant candidates." },
            { num: "06", title: "Support", desc: "Support the hiring process through to the final decision." },
          ].map((s, i) => (
            <Reveal key={s.num} delay={i * 0.05}>
              <div className="grid grid-cols-12 gap-3 md:gap-8 py-6 md:py-7 border-t border-[#E6E2D8] items-baseline" data-testid={`process-step-${s.num}`}>
                <p className="col-span-2 md:col-span-1 font-mono-accent text-sm font-semibold text-[#FF5E1A]">{s.num}</p>
                <h3 className="col-span-10 md:col-span-3 font-display text-lg font-semibold text-[#0F172A]">{s.title}</h3>
                <p className="col-span-12 md:col-span-8 text-sm md:text-base text-[#475569] leading-relaxed md:pl-0 pl-0">{s.desc}</p>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-[#E6E2D8]" />
        </div>
      </div>
    </section>

    {/* CTA (light -> gradient band) */}
    <section className="relative bg-[#0B132B] overflow-hidden">
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 55% 65% at 50% 115%, rgba(255,94,26,0.18), transparent 70%)" }} />
      <div className="container-nb relative py-24 md:py-32 text-center">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-[#F8FAFC] max-w-2xl mx-auto leading-snug">
            Ready to make your next critical hire?
          </h2>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3">
            {["Focused search", "Direct partnership", "Honest feedback"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-[#94A3B8]">
                <Check size={15} className="text-[#FF5E1A]" /> {t}
              </li>
            ))}
          </ul>
          <Link to="/contact" data-testid="employers-cta-discuss" className="btn-primary mt-10 inline-flex">
            Let's Discuss Your Hiring Needs <ArrowRight size={15} />
          </Link>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Employers;
